/**
 * Aplicación web Directorio del Ayuntamiento de Zamora.
 * 
 * Desarrollo e implementación: Jaime León Pereira.
 * Ingeniero Informático de la Unidad TIC.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import ConfirmModal from "./ConfirmModal";
import ModalDepartamento from "../departamentos/ModalDepartamento";
import "../../styles/AdminEntities.css"; 
import AlertModal from "../common/AlertModal";
import useVersionMovil from "../../hooks/useMobileVersion";
/**
 * Componente de administración de departamentos.
 * Permite listar, filtrar, crear, editar y eliminar departamentos,
 * gestionando autenticación mediante Microsoft Entra (MSAL).
 */
const AdminDepartamentos = () => {

  /* ============================
     Estado principal de datos
     ============================ */

  const isMobile = useVersionMovil();
  // Lista de departamentos obtenidos desde la API
  const [departamentos, setDepartamentos] = useState([]);
  
  // Lista de edificios disponibles para asignar a un departamento
  //const [edificios, setEdificios] = useState([]);

  /* ============================
     Estado de modales y acciones
     ============================ */

  // Controla la apertura del modal de confirmación de borrado
  const [modalOpen, setModalOpen] = useState(false);

  // Departamento seleccionado para eliminar
  const [departamentoToDelete, setDepartamentoToDelete] = useState(null);
  
  // Controla la apertura del modal de creación/edición
  const [modalFormOpen, setModalFormOpen] = useState(false);

  // Departamento que se está editando (null si es creación)
  const [departamentoEdit, setDepartamentoEdit] = useState(null);

  /* ============================
     Estado de alertas
     ============================ */

  // Controla la visibilidad del modal de alertas
  const [alertModalOpen, setAlertModalOpen] = useState(false);

  // Mensaje mostrado en la alerta
  const [alertMessage, setAlertMessage] = useState("");
  
  // Tipo de alerta: success | error
  const [alertType, setAlertType] = useState("success"); 
  
  
  // Tipo de acción realizada (create o edit)
  const [actionType, setActionType] = useState("");

  /* ============================
     Estado de filtros de búsqueda
     ============================ */

  // Filtros aplicados a la búsqueda de departamentos
  const [filtros, setFiltros] = useState({
    nombre: "",
    //planta: "",
    //edificio: "",
  });

  // Instancia de MSAL para gestión de autenticación
  const { instance, accounts } = useMsal();

  /**
   * Obtiene de forma silenciosa el access token de Microsoft Entra.
   * Es necesario para autenticar las peticiones a la API.
   */
  const getAccessToken = async () => {
    if (!accounts || accounts.length === 0) return null;
    try {
      const response = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [process.env.REACT_APP_API_SCOPE],
      });
      return response.accessToken;
    } catch (error) {
      console.error("Error obteniendo access token:", error);
      return null;
    }
  };

  /**
   * Obtiene la lista de departamentos desde la API,
   * aplicando los filtros actuales si existen.
   */
  const fetchDepartamentos = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const params = { ...filtros };
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/departamentos`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      const data = Array.isArray(res.data) ? res.data : res.data.content || [];

      const dataOrdenada = data.sort((a, b) =>
        a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
      );

      setDepartamentos(data);
    } catch (err) {
      console.error("Error al obtener departamentos:", err);
      setAlertMessage("Error cargando departamentos desde la API");
      setAlertType("error");
      setAlertModalOpen(true);
    }
  };


  /**
   * Carga inicial de datos al montar el componente.
   */
  useEffect(() => {
    fetchDepartamentos();
    //fetchEdificios();
  }, []);

  /**
   * Si se limpian todos los filtros, se recarga automáticamente
   * la lista completa de departamentos.
   */
  useEffect(() => {
    if (
      filtros.nombre === "" 
    ) {
      fetchDepartamentos();
    }
  }, [filtros]);

  /**
   * Actualiza el estado de los filtros conforme el usuario escribe.
   */
  const handleFiltroChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  /**
   * Ejecuta la búsqueda aplicando los filtros actuales.
   */
  const handleBuscar = (e) => {
    e.preventDefault();
    fetchDepartamentos();
  };

  /**
   * Resetea todos los filtros y muestra todos los departamentos.
   */
  const handleReset = () => {
    setFiltros({ nombre: ""/*, planta: "", edificio: ""*/ });
  };

  /**
   * Abre el formulario en modo creación.
   */
  const handleCrear = () => {
    setDepartamentoEdit(null);
    setModalFormOpen(true);
    setActionType("create");
  };

  /**
   * Abre el formulario en modo edición con los datos del departamento.
   */
  const handleEditar = (dep) => {
    setDepartamentoEdit(dep);
    setModalFormOpen(true);
    setActionType("edit");
  };

  /**
   * Se ejecuta tras guardar correctamente un departamento.
   * Refresca la tabla y muestra un mensaje de confirmación.
   */
  const handleGuardar = async () => {
    await fetchDepartamentos();
    setModalFormOpen(false);
    setAlertMessage(actionType === "create" ? "Departamento creado correctamente" : "Cambios guardados");
    setAlertType("success");
    setAlertModalOpen(true);
  };

  /**
   * Abre el modal de confirmación de borrado.
   */
  const handleDelete = (dep) => {
    setDepartamentoToDelete(dep);
    setModalOpen(true);
  };

   /**
   * Elimina definitivamente el departamento seleccionado.
   */
  const handleConfirmDelete = async () => {
    if (!departamentoToDelete) return;

    try {
      const token = await getAccessToken();
      if (!token) return;

      await axios.delete(`${process.env.REACT_APP_API_URL}/departamentos/${departamentoToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchDepartamentos();
      setAlertMessage(`Departamento "${departamentoToDelete.nombre}" eliminado correctamente.`);
      setAlertType("success");
      setAlertModalOpen(true);
    } catch (err) {
      console.error(err);
      setAlertMessage("Hubo un error al eliminar el departamento.");
      setAlertType("error");
      setAlertModalOpen(true);
    } finally {
      setModalOpen(false);
      setDepartamentoToDelete(null);
    }
  };

  /*****************************************************************
   * Renderizado de la interfaz de administración de departamentos.*
   *****************************************************************/
  return (
    /* Contenedor principal de la vista de administración */
    <div className="admin-personas-container">
      {/* Título de la sección */}
      <div className="text-center fw-bold fs-4">Gestión de Departamentos</div>

      {/* =============================
          Bloque de búsqueda y filtros
          ============================= */}
      <div className="buscador-card p-3 mb-3 me-2">
        <form onSubmit={handleBuscar}>
        
          {/* Campos de filtrado por nombre*/}
          <div className={`filtros-grid ${isMobile ? 'filtros-mobile' : ''} mb-3`} style={{maxWidth: "48vh"}}>
            <input
              type="text"
              name="nombre"
              placeholder="Nombre"
              value={filtros.nombre}
              onChange={handleFiltroChange}
              maxLength={50}
            />
          </div>

          {/* Botonera de acciones del buscador */}
          <div className="botones-buscador">
            <button type="submit" className="btn-modern btn-modern-primary">
              Buscar
            </button>
            <button
              type="button"
              className="btn-modern btn-modern-secondary"
              onClick={handleReset}
            >
              Mostrar todo
            </button>
            {/* Abre el formulario para crear un nuevo departamento */}
            <button
              type="button"
              className="btn-modern btn-modern-create"
              onClick={handleCrear}
            >
              <i className="bi bi-plus-circle"></i> Añadir departamento
            </button>
          </div>
        </form>
      </div>

      {/* =============================
              Tabla de resultados
          ============================= */}
      <div className="table-scroll-wrapper">
        {/* Contenedor con scroll horizontal */}
        <div className="table-scroll">
          <table className="table-modern">
          
            {/* Cabecera de la tabla */}
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Acciones</th>
              </tr>
            </thead>

            {/* Cuerpo de la tabla con los departamentos obtenidos */}
            <tbody>
              {departamentos.map((dep) => (
                <tr key={dep.id}>
                  <td>{dep.id}</td>
                  <td>{dep.nombre}</td>

                  {/* Acciones disponibles para cada departamento */}
                  <td className="actions-cell">
                    
                    {/* Editar departamento */}
                    <button
                      className="btn-action btn-warning"
                      onClick={() => handleEditar(dep)}
                    >
                      <i className="bi bi-pencil"></i>
                    </button>

                    {/* Eliminar departamento */}
                    <button
                      className="btn-action btn-danger"
                      onClick={() => handleDelete(dep)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* =============================
            Modales de la aplicación
          ============================= */}

      {/* Modal de confirmación para eliminación de departamentos */}
      <ConfirmModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmDelete}
        message={`¡Atención! Si eliminas el departamento "${departamentoToDelete?.nombre}" se eliminará este campo en todos los trabajadores asociados. ¿Desea continuar?`}
      />

      {/* Modal de creación y edición de departamentos */}
      <ModalDepartamento
        show={modalFormOpen}
        onClose={() => setModalFormOpen(false)}
        onSave={handleGuardar}
        //edificios={edificios}
        departamento={departamentoEdit}
      />
      
      {/* Modal de alertas para feedback al usuario */}
      <AlertModal
        show={alertModalOpen}
        onClose={() => setAlertModalOpen(false)}
        message={alertMessage}
        type={alertType}
      />
    </div>
  );
};

export default AdminDepartamentos;
